import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import { Popin } from '../../type';
import { chatUrl, isReturnShipmentStep } from '../../helper';
import styles from '../../product-timeline.module.scss';
import UrlActionView from './url-action';
import PopupActionView from './popup-action';
import { Order } from '../../interfaces/myOrder';
import { Product } from '../../interfaces/orderSaleProduct';
import {
  TimelineAction,
  TimelineActionStyle,
  TimelineAnalyticsTracking,
  TimelineStep,
} from '../../interfaces/timeline';
import { Icon, useAnalyticEvents } from '../../fake';

// CLASSES
const link = styles.ordersSales__mainRight__steps__step__box__actions__btnLink;
const reportLink = styles.ordersSales__mainRight__steps__step__box__actions__reportLink;
const button = clsx(
  styles.ordersSales__mainRight__steps__step__box__actions__btn,
  'vc-btn vc-btn--full vc-btn--primary',
);
const buttonOutline = clsx(
  styles.ordersSales__mainRight__steps__step__box__actions__btn,
  'vc-btn vc-btn--full vc-btn--outline',
);
const icon = clsx(styles.ordersSales__mainRight__steps__step__box__actions__btnIcon, 'vc-btn vc-btn--full');

// `type` comes from timeline detail
const getClassByActionStyle = (style?: TimelineActionStyle) => {
  switch (style) {
    case 'primary':
      return button;
    case 'outline':
      return buttonOutline;
    case 'underline':
      return link;
    default:
      return button;
  }
};

const ActionView: React.FC<{
  updatePopin: (p: Popin) => void;
  order: Order | null;
  product: Product;
  step: TimelineStep;
  action: TimelineAction;
  analyticsTracking: TimelineAnalyticsTracking;
}> = ({ updatePopin, order, product, step, action, analyticsTracking }) => {
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');
  const analyticData = { label: order == null ? 'seller' : 'buyer', product_id: product.productId };

  const { status, relistDetails, trackingStep } = step;

  useEffect(() => {
    if (
      (status === 'ongoing' || status === 'pending') &&
      action.identifier === 'qualityControlReport' &&
      action.type != null &&
      action.type !== 'none'
    ) {
      sendAnalyticEvent('timeline_item_assessment_nego', analyticData);
    }
  }, []); // Have no choice for this warning!

  switch (action.identifier) {
    case 'qualityControlReport':
      switch (action.type) {
        case 'button':
          return (
            <UrlActionView
              url={action.url}
              className={clsx(button, icon)}
              newTab={false}
              onClick={() => {
                sendAnalyticEvent('timeline_view_report', analyticData);
              }}
              dataCy={`action-${action.identifier}`}
            >
              <Icon
                className={styles.ordersSales__mainRight__steps__step__box__actions__btnIcon__icon}
                name={'invoice-default'}
              />
              {action.label || action.title}
            </UrlActionView>
          );
        case 'link':
          return (
            <UrlActionView
              url={action.url}
              className={clsx(icon, link)}
              newTab={false}
              onClick={() => {
                sendAnalyticEvent('timeline_view_report', analyticData);
              }}
              dataCy={`action-${action.identifier}`}
            >
              <Icon
                className={styles.ordersSales__mainRight__steps__step__box__actions__btnIcon__icon}
                name={'invoice-default'}
              />
              {action.title}
            </UrlActionView>
          );
        case 'none':
          return null;
      }
    case 'relist':
      if (relistDetails) {
        const onRelist = () => {
          updatePopin({ kind: 'Relist', order, product, relistDetails: relistDetails });
          sendAnalyticEvent('timeline_relist_item', analyticData);
        };
        return (
          <PopupActionView
            className={getClassByActionStyle(action.style)}
            onClick={onRelist}
            dataCy={`action-${action.identifier}`}
          >
            {action.title}
          </PopupActionView>
        );
      } else {
        return <></>;
      }
    case 'returnForm':
      const onReturnForm = () => {
        if (order) {
          updatePopin({
            kind: 'ReturnForm',
            product,
            order,
            returnReasons: action.returnReasons,
          });
          sendAnalyticEvent('timeline_b2c_return', analyticData);
        }
      };
      return (
        <PopupActionView
          className={buttonOutline}
          onClick={onReturnForm}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      );
    case 'confirmDelivery':
      const onConfirmDelivery = () => {
        if (action.deliveryType === 'home') {
          sendAnalyticEvent('timeline_home_delivery_confirmation', analyticData);
        } else {
          sendAnalyticEvent('timeline_pickup_confirmation', analyticData);
        }

        updatePopin({
          kind: 'ConfirmDelivery',
          product,
          order,
          confirmDeliveryDetails: action,
        });
      };
      return (
        <PopupActionView
          className={button}
          onClick={onConfirmDelivery}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      );
    case 'download':
      return (
        <UrlActionView
          url={action.url}
          className={button}
          newTab={true}
          onClick={() => {
            if (action.type && action.type === 'returnLabel') {
              sendAnalyticEvent('timeline_download_label_return', analyticData);
            } else {
              sendAnalyticEvent('timeline_download_label', analyticData);
            }
          }}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'contact':
      if (action.customerId != null) {
        return (
          <UrlActionView
            // Note: from the api, customerId === sellerId, but for create a chat url we need buyer id, the user
            url={chatUrl(action.customerId, product.productId)}
            className={clsx(icon, 'vc-btn--outline')}
            newTab={true}
            onClick={() => {
              sendAnalyticEvent('timeline_bs_chat', action.tracking);
            }}
            dataCy={`action-${action.identifier}`}
          >
            <Icon
              className={styles.ordersSales__mainRight__steps__step__box__actions__btnIcon__icon}
              name={'chat'}
            />
            {action.title}
          </UrlActionView>
        );
      } else {
        return null;
      }
    case 'cancel':
      return (
        <UrlActionView
          url={action.url}
          className={clsx(icon, link)}
          newTab={false}
          onClick={() => {
            sendAnalyticEvent('timeline_cancel_sale', analyticData);
          }}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'withdrawReturn':
      return (
        <UrlActionView
          url={action.url}
          className={getClassByActionStyle(action.style)}
          newTab={false}
          onClick={() => {
            sendAnalyticEvent('timeline_withdraw_and_return', action.tracking);
          }}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'relist':
      if (relistDetails) {
        const onRelist = () => {
          updatePopin({ kind: 'Relist', order, product, relistDetails: relistDetails });
          sendAnalyticEvent('timeline_relist_item', analyticData);
        };
        return (
          <PopupActionView
            className={getClassByActionStyle(action.style)}
            onClick={onRelist}
            dataCy={`action-${action.identifier}`}
          >
            {action.title}
          </PopupActionView>
        );
      } else {
        return <></>;
      }
    case 'report':
      const handleReportIssue = () => {
        if (action.type === 'report') {
          sendAnalyticEvent('timeline_report_issue', {
            ...analyticData,
            property: trackingStep,
            value: analyticsTracking.sellerStatus?.toUpperCase() === 'C2C' ? '0' : '1',
          });
        } else if (action.type === 'viewIssue') {
          sendAnalyticEvent('timeline_view_issue', analyticData);
        }
      };

      return (
        <UrlActionView
          url={action.url}
          className={reportLink}
          newTab={false}
          onClick={() => handleReportIssue()}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'getPaid':
      return (
        <UrlActionView
          url={action.url || ''}
          className={button}
          newTab={false}
          onClick={() => {
            sendAnalyticEvent('timeline_get_paid', {
              ...analyticData,
              ...action.tracking,
            });
          }}
          dataCy={`action-${action.identifier}`}
        >
          <div>{action.title}</div>
        </UrlActionView>
      );
    case 'shippingLabel': {
      const isReturnShipment = isReturnShipmentStep(step.identifier);
      const actionTypes: Array<string | undefined> = isReturnShipment
        ? ['getShippingLabel', 'editShippingDetails']
        : ['editShippingDetails'];

      return actionTypes.includes(action.type) ? (
        <PopupActionView
          className={clsx(icon, action.type === 'editShippingDetails' ? 'vc-btn--outline' : 'vc-btn--primary')}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      ) : null;
    }
    case 'uploadInvoice':
      return (
        <PopupActionView
          className={clsx(icon, 'vc-btn--primary')}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      );
    case 'updateAddress': {
      return (
        <PopupActionView
          className={clsx(icon, 'vc-btn--outline')}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      );
    }
    case 'accept':
      return (
        <UrlActionView
          url={action.url}
          className={getClassByActionStyle(action.style)}
          newTab={false}
          // onClick={() => showAcceptOrderConfirmationModal()}
        >
          <div>{action.title}</div>
        </UrlActionView>
      );
    default:
      return null;
  }
};

export default ActionView;
